import React, { useState, useEffect } from 'react';
import { getProducts } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Check, Loader2 } from 'lucide-react';

const ProductSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load products');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section id="products" className="section-spacing bg-blush">
        <div className="max-w-7xl mx-auto text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto accent-gold" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="products" className="section-spacing bg-blush">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="section-spacing bg-blush">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-serif font-semibold text-foreground mb-6">
            Signature Collection
          </h2>
          <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
            Discover our carefully designed pieces that combine comfort, support, and confidence
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <Card 
              key={product.id} 
              className="card-hover bg-white border-0 shadow-lg overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Product Image Placeholder */}
              <div className="h-64 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <span className="text-6xl font-serif text-primary/40">WM</span>
              </div>

              <CardHeader>
                <CardTitle className="text-2xl font-serif text-foreground">
                  {product.name}
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground">
                  {product.description}
                </CardDescription>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {product.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 accent-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-sans text-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="flex items-center justify-between">
                <span className="text-3xl font-serif font-semibold text-foreground">
                  ${product.price}
                </span>
                <Button className="bg-accent-gold hover:bg-accent-gold/90 text-white font-sans font-semibold text-sm tracking-wider">
                  Add to Cart
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
